import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnDestroy, signal, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {MatIcon} from '@angular/material/icon';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {DeleteDialogComponent} from '@app/dialogs/object-delete-dialog/object-delete-dialog.component';
import {SubsectionScrollDirective} from '@app/directives/subsection-scroll/subsection-scroll.directive';
import {PermissionsTableComponent} from '@app/permissions/permissions-table/permissions-table.component';
import {
  addPermissionRow,
  buildPatchPayload,
  mapUsersToPermissionRows,
  PermissionRow,
  PermissionsContext,
  removePermissionRow,
  updatePermissionRow,
} from '@app/permissions/permissions.mapper';
import {pdeRoutes} from '@app/routing/routes';
import {ProjectResponse, ProjectsService, ProjectUserRoleReference} from '@backend';
import {IndexService} from '@middleware';
import {PrivateProjectPageData, ProjectBaseModel} from '@project/project-base.model';
import {SectionHeaderActions} from '@shared/components/section-header-actions/section-header-actions';
import {ProjectRoleOptions} from '@shared/labels/project-role';
import extractError from '@utilities/error-extractor';
import {firstValueFrom, lastValueFrom, Subscription} from 'rxjs';

@Component({
  selector: 'app-project-access',
  imports: [
    MatRadioButton,
    MatRadioGroup,
    ReactiveFormsModule,
    MatIcon,
    MatButton,
    RouterLink,
    PermissionsTableComponent,
    SubsectionScrollDirective,
  ],
  templateUrl: './project-access.component.html',
})
export class ProjectAccessComponent implements OnDestroy, SectionHeaderActions {
  readonly project = signal<ProjectBaseModel | null>(null);
  readonly privateProject = signal<ProjectResponse | null>(null);
  readonly permissionRows = signal<PermissionRow[]>([]);

  permissionsContext!: PermissionsContext;
  private projectsService!: ProjectsService;

  visibilityForm: FormGroup;
  private visibilityChangedSubscription: Subscription;

  @ViewChild(PermissionsTableComponent) permissionsTable: PermissionsTableComponent;

  constructor(
    private readonly route: ActivatedRoute,
    fb: FormBuilder,
    private readonly indexService: IndexService,
    private readonly snackbarService: MatSnackBar,
    private readonly dialogService: MatDialog,
    private readonly router: Router,
  ) {
    this.visibilityForm = fb.group({
      visibility: ['team'],
    });

    route.data.subscribe(d => {
      const {project, privateProject, projectsService} = <PrivateProjectPageData>d['projectData'];

      this.project.set(project);
      this.privateProject.set(privateProject);
      this.projectsService = projectsService;

      this.permissionsContext = {
        type: 'project',
        id: privateProject.id,
        editable: true,
        showEditButton: false,
        orgName: privateProject.storage_organization.full_name,
      };

      this.permissionRows.set(
        mapUsersToPermissionRows(privateProject.project_users, this.permissionsContext, ProjectRoleOptions),
      );
      const visibilityControl = this.visibilityForm.get('visibility');
      visibilityControl?.setValue(privateProject.visible_to_all_users ? 'public' : 'team', {emitEvent: false});
      if (visibilityControl && !this.visibilityChangedSubscription) {
        this.visibilityChangedSubscription = visibilityControl.valueChanges.subscribe(v => this.onVisibilityChange(v));
      }
    });
  }

  ngOnDestroy(): void {
    if (this.visibilityChangedSubscription) {
      this.visibilityChangedSubscription.unsubscribe();
    }
  }

  async onSavePermission(row: PermissionRow) {
    const project = this.privateProject();
    if (!project) return;

    this.permissionRows.update(rows =>
      rows.some(r => r.email === row.email) ? updatePermissionRow(rows, row) : addPermissionRow(rows, row),
    );

    const payload = buildPatchPayload(this.permissionsContext, this.permissionRows()) as ProjectUserRoleReference[];

    try {
      await lastValueFrom(
        this.projectsService.patchProject(project.id, {
          users: payload,
        }),
      );

      await lastValueFrom(
        this.indexService.syncProjectInIndex(this.permissionsContext.id, {
          storage_organization: this.project().storage_organization.name,
        }),
      );
    } catch (ex) {
      this.snackbarService.open(
        `Couldn't set permissions on "${project.name}": ${extractError(<HttpErrorResponse>ex)}`,
        undefined,
        {
          duration: 15000,
          panelClass: 'snackbar-error',
        },
      );
    }
    const refreshed = await firstValueFrom(this.projectsService.getProject(project.id));
    this.privateProject.set(refreshed);
    this.permissionRows.set(
      mapUsersToPermissionRows(refreshed.project_users, this.permissionsContext, ProjectRoleOptions),
    );
  }

  async onDeletePermission(row: PermissionRow) {
    const project = this.privateProject();
    if (!project) return;

    this.permissionRows.update(rows => removePermissionRow(rows, row.email));

    const payload = buildPatchPayload(this.permissionsContext, this.permissionRows()) as ProjectUserRoleReference[];
    try {
      await lastValueFrom(
        this.projectsService.patchProject(project.id, {
          users: payload,
        }),
      );

      await lastValueFrom(
        this.indexService.syncProjectInIndex(this.permissionsContext.id, {
          storage_organization: this.project().storage_organization.name,
        }),
      );
    } catch (ex) {
      this.snackbarService.open(
        `Couldn't set permissions on "${project.name}": ${extractError(<HttpErrorResponse>ex)}`,
        undefined,
        {
          duration: 15000,
          panelClass: 'snackbar-error',
        },
      );
    }

    const refreshed = await firstValueFrom(this.projectsService.getProject(project.id));
    this.privateProject.set(refreshed);
    this.permissionRows.set(
      mapUsersToPermissionRows(refreshed.project_users, this.permissionsContext, ProjectRoleOptions),
    );
  }

  async onVisibilityChange(value: 'public' | 'team') {
    const project = this.privateProject();
    if (!project) return;

    const isPublic = value === 'public';

    try {
      await lastValueFrom(
        this.projectsService.patchProject(project.id, {
          visible_to_all_users: isPublic,
        }),
      );

      await lastValueFrom(
        this.indexService.syncProjectInIndex(this.permissionsContext.id, {
          storage_organization: this.project().storage_organization.name,
        }),
      );
      this.snackbarService.open(
        `"${project.name}" is now ${isPublic ? 'available on' : 'withdrawn from'} the Public Data Commons.`,
        undefined,
        {duration: 5000},
      );
    } catch (ex) {
      this.snackbarService.open(
        `Couldn't make "${project.name}" ${isPublic ? 'public' : 'private'}: ${extractError(<HttpErrorResponse>ex)}`,
        undefined,
        {
          duration: 15000,
          panelClass: 'snackbar-error',
        },
      );
    }

    this.privateProject.set(await lastValueFrom(this.projectsService.getProject(this.project().id)));
    this.visibilityForm
      .get('visibility')
      ?.setValue(this.privateProject().visible_to_all_users ? 'public' : 'team', {emitEvent: false});
  }

  async deleteProject() {
    const project = this.privateProject();
    if (!project) return;
    if (
      !(await lastValueFrom(
        this.dialogService
          .open(DeleteDialogComponent, {data: {objectName: project.name, objectKind: 'project'}})
          .afterClosed(),
      ))
    )
      return;
    try {
      await lastValueFrom(this.projectsService.deleteProject(project.id, {immediate: false}));

      await lastValueFrom(
        this.indexService.syncProjectInIndex(this.permissionsContext.id, {
          storage_organization: this.project().storage_organization.name,
        }),
      );
    } catch (ex) {
      this.snackbarService.open(
        `Couldn't delete "${project.name}": ${extractError(<HttpErrorResponse>ex)}`,
        undefined,
        {
          duration: 15000,
          panelClass: 'snackbar-error',
        },
      );
      return;
    }
    this.snackbarService.open(`Successfully deleted "${project.name}".`, undefined, {duration: 15000});
    await this.router.navigate(['/private-data-enclave/']);
  }

  public sectionHeaderActions = signal([
    {
      target: '/help/private-data-enclave/edit-private-project-access',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  protected readonly pdeRoutes = pdeRoutes;
}
